import React, {useContext, useState, useEffect} from 'react';
import ReactDOM from 'react-dom';
import {AppBar, Toolbar, Typography, Menu, MenuItem, Tooltip, CircularProgress} from '@mui/material';
import {Box} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import jwtDecode from "jwt-decode";
import {
    colorEasyGreen,
    colorEasyRed, colorGreen,
    colorLogo,
    colorLogoCenter, colorMenuEdge,
    colorText, intensePaper, logoText
} from "./theme.js";
import {
    getQueryParamValue,
    isMobile,
    navigateToBaseURL,
    parseKBSubdomain, removeFromUrl, RESOURCE,
} from "./utils";

import { Logo } from "./Logo";
import {clientUserData, clientUserDataLoaded, goHome, isSharedApp, kbUserData} from "../utils";
import {
    PermIdentity,
    ExitToApp,
    Wallet,
    Forum,
    PriceChangeOutlined, LightMode, DarkMode, ArrowRight
} from '@mui/icons-material';
import {useNavigate} from "react-router-dom";
import {UserAvatar} from "../User/UserAvatar";
import CloseIcon from "@mui/icons-material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import {RenderBalances} from "./Balances";
import NotificationsIcon from '@mui/icons-material/Notifications';
import Badge from '@mui/material/Badge';
import {NotificationsItemsList} from "./Notifications";
import {ChatsList} from "./ChatsHistory";
import {getUserNotifications, resetUnread} from "../API/APP/AppAPI";
import {getKB} from "../API/KB/KBAPI";
import {useTheme} from "./ThemeContext";
import {TypingLabel} from "../Components/TypingLabel";
import {isCustomChat} from "./ChatsHistoryUtils";

const Navbar = ({onLogout, chatId, setOpen, userProfile, open, balances, loadingBalances, kbSubdomain,
kbChats, setKBChats, isStreaming, navigateToChat, unreadMessages, setUserProfile, renderSettings, ChatData, setChatData
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const menuOpen = Boolean(anchorEl);
    const navigate = useNavigate();
    const [cacheRevision, setCacheRevision] = useState(null);
    const [picture, setPicture] = useState(null);
    const [email, setEmail] = useState(null);
    const [KBData, setKBData] = useState(null);
    const [notifAnchorEl, setNotifAnchorEl] = useState(null);
    const [phoneNotVerified, setPhoneNotVerified] = useState(false);


    const [userNotifications, setUserNotifications] = useState(null);

    const [chatsAnchorEl, setChatsAnchorEl] = useState(null);
    const notifMenuOpen = Boolean(notifAnchorEl);
    const chatsMenuOpen = Boolean(chatsAnchorEl);

    useEffect(() => {
        getKB().then(KB => setKBData(KB))
    }, [])

    useEffect(() => {
        // This empty useEffect will cause a re-render when unreadMessages changes
    }, [unreadMessages]);

    const handleChatsMenuOpen = (event) => {
        setChatsAnchorEl(event.currentTarget);
    };

    const handleChatsMenuClose = () => {
        setChatsAnchorEl(null);
    };

    const handleNotifMenuOpen = (event) => {
        setNotifAnchorEl(event.currentTarget);
        if (userProfile?.unread > 0) {
            setUserProfile(prev => {
                return {
                    ...prev,
                    unread: 0
                }
            })
        }
        resetUnread();
        getUserNotifications().then(data => {
            setUserNotifications(data)

        })

    };

    const handleNotifMenuClose = () => {
        setNotifAnchorEl(null);
    };

    const handleDrawerOpenClose = () => {
        if (!isMobile) {
            setOpen(!open);
        }
    };

    useEffect(() => {
        const handleCacheInvalidation = (event) => {
            setCacheRevision(event.detail.cacheRevision);
        };
        window.addEventListener('userImageCacheInvalidated', handleCacheInvalidation);
        return () => {
            window.removeEventListener('userImageCacheInvalidated', handleCacheInvalidation);
        };
    }, []);

    const menuItemStyle = { padding: '10px 30px' };
    const parsedSubdomain = parseKBSubdomain();
    const isKBSubdomain =  !!parsedSubdomain;

    // set early picture from JWT
    useEffect(() => {
        if (getQueryParamValue('pnv')) {
            navigate('/')
            setPhoneNotVerified(true)
        }

        if (clientUserDataLoaded?.picture && clientUserDataLoaded?.email) {
            setPicture(clientUserDataLoaded?.picture)
            setEmail(clientUserDataLoaded?.email)
        }
    }, []);

    useEffect(() => {
        if (isKBSubdomain) {
            setPicture(kbUserData()?.userPicture || '')
            setEmail(kbUserData()?.email)
        } else {
            try {
                const token = localStorage.getItem("clientJWT");
                setPicture((userProfile?.picture || (userProfile?.picture === '' && jwtDecode(token)?.picture)))
                setEmail(userProfile?.email)
            } catch(e) {
                window.dispatchEvent(new CustomEvent('logout'));
            }
        }
    }, [userProfile]);

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const runningOutOfTokens = balances?.find(o => o.resourceId === RESOURCE.tokens)?.balance < 30000;

    const { toggleTheme, theme } = useTheme();
    const colors = (getQueryParamValue('colors') || '').split(',');
    const textColors = (getQueryParamValue('text') || '').split(',');
    const totalUnread = Object.values(unreadMessages)
        .reduce((accumulator, currentValue) => (accumulator + currentValue),0)

    return (
        <AppBar elevation={1} position="fixed" sx={{zIndex: (theme) => theme.zIndex.drawer + 1}}>
            <Box
                sx={{
                    backgroundColor: theme => theme.palette.background.paper,
                    // minHeight: '100vh',
                }}
            >
                <Toolbar>

                    {isMobile && isKBSubdomain && <IconButton
                        style={{marginLeft: '-12px'}}
                        size="large"
                        aria-label="show chat messages"
                        color="inherit"
                        onClick={handleChatsMenuOpen}
                    >
                        <Badge
                            max={999}
                            key={`totalUnread-${totalUnread}`}
                            sx={{
                                '& .MuiBadge-badge': {
                                    animation: totalUnread ? 'bubble 1s ease-in-out' : 'none',
                                },
                            }}
                            badgeContent={totalUnread}
                            color="error">
                            <Forum />
                            {/*{ChatData?.chatIcon ? <span style={{fontSize: 22}}>{ChatData.chatIcon}</span> : <Forum /> }*/}
                        </Badge>
                    </IconButton>}

                    {!isMobile && <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={handleDrawerOpenClose}
                        edge="start"
                        sx={{mr: 2}}
                    >
                        {open ? <CloseIcon /> : <MenuIcon />}
                    </IconButton>}

                    <div style={{width: (isMobile) ? '8px' : (!open ? '0px' : '260px'), transition: 'width 0.130s linear'}} ></div>

                    {KBData?.kbTitle
                        ? (
                            <>
                                <div style={{ marginLeft: 50, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', position: isMobile ? 'absolute' : undefined }}>
                                    <Typography variant="h5" sx={{
                                        fontSize: 18,
                                        fontFamily: "'Roboto', sans-serif",
                                        fontWeight: 600,
                                        color: colorText,
                                        cursor: 'pointer',
                                        maxWidth: isMobile ? 180 : 'none',
                                        overflow: isMobile ? 'hidden' : 'visible',
                                        textOverflow: isMobile ? 'ellipsis' : 'clip',
                                        whiteSpace: isMobile ? 'nowrap' : 'normal'
                                    }}>
                                        {!isMobile && ChatData?.chatIcon} { isCustomChat(ChatData) ? ChatData?.title : KBData?.kbTitle}
                                    </Typography>
                                </div>
                            </>
                        )
                        : !isKBSubdomain && (<>
                            <IconButton
                                style={{marginLeft: '-18px'}}
                                color="inherit"
                                aria-label="logo"
                                onClick={() => goHome(navigate)}
                                edge="start"
                                sx={{mr: 2}}
                            >
                                {getQueryParamValue('colors')
                                    ? <Logo  width="24" height="24" colors={(getQueryParamValue('colors') || '').split(',').map(color => `#${color.trim()}`)}/>
                                    : <Logo  width="24" height="24" colors={[colorLogo,colorLogo,colorLogo,colorLogo,colorLogo,colorLogo,colorLogoCenter]}/>}
                            </IconButton>

                        <Typography variant="h5" sx={{
                            fontSize: isMobile ? 18 : 20,
                            marginLeft: '-12px',
                            marginTop: '2px',
                            fontWeight: 600,
                            cursor: 'pointer'
                        }}
                                    onClick={() => goHome(navigate)}
                        >
                            {"OpenKBS".split('').map((letter, index) => {
                                const colorIndex = index % textColors.length;
                                // textColors[colorIndex].trim()
                                return (
                                    <span key={index} style={{ color: getQueryParamValue('text') ? `#${getQueryParamValue('text')}` : logoText }}>{letter}</span>
                                );
                            })}
                        </Typography>
                        </>)}

                    <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                        <div style={{visibility: renderSettings?.disableBalanceView ? 'hidden' : undefined}}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginRight: '16px' }}>
                                {/* Conditional rendering based on your existing logic */}
                                {(!kbSubdomain || !isSharedApp) && <span style={{ marginRight: isMobile ? 10 : 0 }}>{RenderBalances({ balances, loadingBalances, kbSubdomain })}</span>}
                            </Box>
                        </div>

                        {isMobile && kbSubdomain && <>
                        <Menu
                            anchorEl={chatsAnchorEl}
                            open={chatsMenuOpen}
                            onClose={handleChatsMenuClose}
                            slotProps={{
                                paper: {
                                    sx: {
                                        backgroundColor: intensePaper
                                    }
                                }
                            }}
                        >
                            <ChatsList {...{kbChats, setKBChats, isStreaming, navigateToChat, unreadMessages, ChatData, setChatData}}
                                       onClickChat={handleChatsMenuClose}
                            />
                        </Menu></>}

                        {!isKBSubdomain && userProfile?.email && <>
                            <IconButton
                                style={{ marginRight: '8px' }}
                                size="large"
                                aria-label="show new notifications"
                                color="inherit"
                                onClick={handleNotifMenuOpen}
                            >
                                <Badge badgeContent={userProfile?.unread} color="error">
                                    <NotificationsIcon />
                                </Badge>
                            </IconButton>
                            <Menu
                                anchorEl={notifAnchorEl}
                                open={notifMenuOpen}
                                onClose={handleNotifMenuClose}
                                PaperProps={{
                                    sx: {
                                        backgroundColor: colorMenuEdge,
                                    }
                                }}
                            >
                                {/* Check if userNotifications is null (loading state) */}
                                {userNotifications === null ? (
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        margin: 30,
                                        minWidth: 300,
                                        minHeight: 100
                                    }}>
                                        <CircularProgress />
                                    </div>
                                ) : userNotifications.length === 0 ? (
                                    // Check if userNotifications is an empty array (no notifications)
                                    <div style={{ margin: 30, minWidth: 300, minHeight: 100 }}>There are no notifications yet.</div>
                                ) : (
                                    // Render notifications if there are any
                                    <NotificationsItemsList notifications={userNotifications} unread={userProfile?.unread} />
                                )}
                            </Menu>
                        </>}

                        {typeof picture === 'string' &&
                            <div>
                                <Badge
                                    overlap="circular"
                                    style={{cursor: 'pointer'}}
                                    onClick={handleMenu}
                                    anchorOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    badgeContent={(
                                        // (phoneNotVerified ? <span>$1&nbsp;bonus</span> : 0) ||
                                        (runningOutOfTokens ? 'Credits' : 0)
                                    )}
                                    color="success"
                                    invisible={!runningOutOfTokens && !phoneNotVerified}
                                >
                                    <UserAvatar picture={picture} onClick={handleMenu} style={{ cursor: 'pointer' }} />
                                </Badge>
                            </div>
                        }
                    </Box>
                    <Menu
                        anchorEl={anchorEl}
                        open={menuOpen}
                        onClose={handleClose}
                        onClick={handleClose}
                    >
                        <MenuItem disabled={true} selected={false} sx={menuItemStyle}>
                            {email}
                        </MenuItem>

                        <MenuItem onClick={toggleTheme} sx={menuItemStyle}>
                            {theme.palette.mode  === 'dark' ? <LightMode sx={{ mr: 1 }} /> : <DarkMode sx={{ mr: 1 }} /> }
                            {theme.palette.mode  === 'dark' ? 'Light' : 'Dark' } Theme
                        </MenuItem>

                        <MenuItem onClick={() => !isKBSubdomain ? navigate('/profile') : navigateToBaseURL('/profile')}
                                  sx={menuItemStyle}>
                            <PermIdentity sx={{ mr: 1 }} />
                            Profile
                        </MenuItem>
                        <MenuItem
                            onClick={() => !isKBSubdomain
                                ? navigate('/billing')
                                : navigateToBaseURL('/billing' + (phoneNotVerified ? '?showPhoneVerification=1' : ''),
                                    (phoneNotVerified || runningOutOfTokens))
                            }
                            sx={{
                                ...menuItemStyle,
                                backgroundColor: 'inherit',
                                color: (phoneNotVerified || runningOutOfTokens) ? colorGreen : 'inherit'
                            }}
                        >
                            <Wallet sx={{ mr: 1, color: (phoneNotVerified || runningOutOfTokens) ? colorGreen : undefined }} />
                            Billing

                            {phoneNotVerified && <span style={{ marginLeft: '8px', color: colorGreen }}>
                            <ArrowRight style={{marginBottom: -7, color: colorGreen}}/>Activate Bonus</span>}

                            {runningOutOfTokens && !phoneNotVerified && <span style={{ marginLeft: '8px', color: colorGreen }}>
                            <ArrowRight style={{marginBottom: -7, color: colorGreen}}/> Buy Credits</span>}
                        </MenuItem>
                        <MenuItem
                            onClick={() => !isKBSubdomain ? navigate('/services-pricing') : navigateToBaseURL('/services-pricing')} sx={menuItemStyle}>
                            <PriceChangeOutlined sx={{ mr: 1 }} />
                            Pricing
                        </MenuItem>
                        <MenuItem onClick={() => onLogout({fromLogoutButton: true})} sx={menuItemStyle}>
                            <ExitToApp sx={{ mr: 1 }} />
                            {!isKBSubdomain ? `Logout` : `Logout from ${document.title}`}
                        </MenuItem>

                    </Menu>
                </Toolbar>
            </Box>
        </AppBar>
    );
};

export default Navbar;